/* eslint-disable react/no-unescaped-entities */
import cx from 'classnames'
import React from 'react'

import SiteAddress from '@cinch/oxd-components/booking-site-address/src'
import { Tertiary } from '@cinch/oxd-components/button/src'
import { ExternalLink } from '@cinch/oxd-components/icons/src'
import { Tick } from '@cinch/oxd-components/icons/src'
import Link, { Appearance } from '@cinch/oxd-components/link/src'
import List, { ListItem } from '@cinch/oxd-components/list/src'
import LoadingSpinner from '@cinch/oxd-components/loading-spinner/src'
import staticMapSrc from '@cinch/oxd-components/location-widget/src/static-map'
import Panel from '@cinch/oxd-components/panel/src'
import { Text, TextStyles } from '@cinch-labs/design-system'

import { Site } from '@cinch/oxd-pages/wbac/src/types'

import * as styles from './booking-confirmation.module.css'

interface BookingConfirmationProps {
  isLoading?: boolean
  site?: Site
  date: string
  timeslot: string
  valuationAmount: number
  valuationId: number
  adminFee: number
  i18n: {
    contact: string
    email: string
    phone: string
    aware: string
    home: string
    valuation: string
    reference: string
    fee: string
    transfer: string
    documents: string
    id: string
    finance: string
    about: string
  }
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  isLoading,
  site,
  date,
  timeslot,
  valuationAmount,
  valuationId,
  adminFee,
  i18n: { contact, email, phone, aware, valuation, reference, fee, transfer, documents, id, finance, about },
}) => {
  const timeString = timeslot.replace(/:00$/, '')
  const dateString = new Date(date).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  if (!site || isLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <div className={styles.gdGridContainer}>
        <div className={cx(styles.gdTime, styles.datetime)}>
          <span className={styles.time}>{timeString}</span>
          <span className={styles.date}>{dateString}</span>
        </div>
        <Panel title={'Your Appointment'} className={styles.gdSite}>
          <div className={styles.introduction} dangerouslySetInnerHTML={{ __html: site.introductionParagraph }} />
          <div className={styles.address}>
            <SiteAddress site={site} />
          </div>
          <Text oxford={true} element="h3" type={TextStyles.menuTitle}>
            {contact}
          </Text>
          <div className={styles.contact}>
            <Text oxford={true} element="h4" className={styles.contactHeading} type={TextStyles.body2}>
              {email}
            </Text>
            <Text oxford={true} element="p" type={TextStyles.body2}>
              <a href={`mailto:${site.emailAddress}`}>{site.emailAddress}</a>
            </Text>
            <Text oxford={true} element="h4" className={styles.contactHeading} type={TextStyles.body2}>
              {phone}
            </Text>
            <Text oxford={true} element="p" type={TextStyles.body2}>
              {site.directPhoneNumber}
            </Text>
            <Text oxford={true} element="p" type={TextStyles.vehicleBody}>
              {aware}
            </Text>
          </div>
          <Text oxford={true} element="h3" type={TextStyles.menuTitle}>
            {valuation}
          </Text>
          <Text oxford={true} element="p" type={TextStyles.body2}>{`£${valuationAmount}`}</Text>
          <Text oxford={true} element="h3" type={TextStyles.menuTitle}>
            {reference}
          </Text>
          <Text oxford={true} element="p" type={TextStyles.body2}>
            {valuationId}
          </Text>
          <Text oxford={true} element="h3" type={TextStyles.menuTitle}>
            {fee}
          </Text>
          <Text oxford={true} element="p" type={TextStyles.body2}>
            £{adminFee.toFixed(2)}
          </Text>
          <Text oxford={true} element="p" type={TextStyles.body2}>
            {transfer}
          </Text>
        </Panel>
        <Panel title={'Appointment Checklist'} className={styles.panel}>
          <Text oxford={true} element="h4" type={TextStyles.menuTitle}>
            {documents}
          </Text>
          <List>
            <ListItem bulletIcon={Tick}>V5 document (or logbook)</ListItem>
            <ListItem bulletIcon={Tick}>Car service history if it has one</ListItem>
          </List>
          <Text oxford={true} element="h4" type={TextStyles.menuTitle}>
            {id}
          </Text>
          <List>
            <ListItem bulletIcon={Tick}>Photographic proof ID (e.g. driving license, passport)</ListItem>
            <ListItem bulletIcon={Tick}>
              Proof of address for the last 9 months (e.g. utility bill, bank statement, council tax bill)
            </ListItem>
          </List>
          <Text oxford={true} element="h4" type={TextStyles.menuTitle}>
            {finance}
          </Text>
          <List>
            <ListItem bulletIcon={Tick}>Your bank account details so we can pay you</ListItem>
            <ListItem bulletIcon={Tick}>
              If there is any outstanding finance, a letter from the finance company confirming their settlement figure
            </ListItem>
          </List>
        </Panel>
        <Panel title={about} className={styles.panel}>
          <Text oxford={true} element="p" type={TextStyles.body2}>
            Your valuation is based on the assumptions and any damage you have entered. We always carry out an on-site
            inspection of a vehicle before we offer to buy it. If we consider that its value is affected by any aspect
            of its history or condition, including any unusual feature or customisation, which was not apparent to us
            when we gave the online valuation, the price that we offer for it will differ from the online valuation.
            Based on a purchase price of £{valuationAmount}, a transaction fee of £{adminFee.toFixed(2)} including VAT
            would apply.
          </Text>
          <Text oxford={true} element="p" type={TextStyles.body2}>
            We guarantee we are willing to buy every car we value. If there is a change in the market value of your car
            resulting in a reduced valuation, then we endeavour to let you know what the new valuation is before your
            appointment, so that you have the opportunity to cancel, if you choose to do so. The price you are given
            when you get your online valuation is valid for 24-hours. If you sell your car to us within 24-hours of your
            valuation, and your car is in the condition that you say it is, we guarantee we'll pay the price we've
            quoted you online. If your appointment is outside of the 24-hour price guarantee period a new valuation for
            your vehicle will be required on-site. Our standard payment terms utilise Future Dated Faster Payment which
            takes 4 working days after the day of sale to arrive at your bank account. For an additional fee of £24.75
            we can provide you with the option of receiving payment via next working day payment.
          </Text>
        </Panel>
        <Panel title={'Directions'} subtitle={site.locationDescription} className={styles.gdDirections}>
          <Tertiary
            className={styles.mapLink}
            type="button"
            onClick={() => window.open(site.mapUrl, '_blank')}
            icon={ExternalLink}
          >
            Open in Google Maps
          </Tertiary>
          <img
            className={styles.map}
            alt={site.metaPlaceName}
            src={staticMapSrc({ zoom: 16, center: `${site.locationLat},${site.locationLng}`, size: '400x350' })}
          />
        </Panel>
      </div>
      <Link oxford={true} appearance={Appearance.primaryButton} to="/">
        Go back home
      </Link>
    </>
  )
}

export default BookingConfirmation
